/**
 * The boundary: supplement responses turned into what the screens read.
 *
 * Its own file rather than private to `supplementPlansService` so that the
 * suites can reach it. A mock stands in for that service, and a mock that
 * imported the module it replaces would be a cycle — so the conversions the
 * mock wants to run its fixtures through live here, one import away from
 * both. The same arrangement `exerciseWire` makes, for the same reason.
 *
 * Four vocabularies meet here and nowhere else: `ACTIVE`/`DRAFT` against the
 * app's `sent`/`draft`, `BEFORE`/`AFTER` against `Before`/`After`,
 * `EVERYDAY`/`SPECIFIC_DAYS` against `everyday`/`days`, and `MON` against the
 * index Monday has in `DAY_LABELS`. Every one of them is the backend's word
 * on the left and the app's on the right, which is what a boundary is for.
 *
 * Nothing else should read these. The service re-exports what it needs, and
 * the rest of the module deals in `SupplementPlanSummary` and `PlanSupplement`.
 */

import { DAY_LABELS } from "./data/supplementOptions";
import { createId, isDoseComplete, shortPlanName } from "./supplementMapping";
import type {
  DoseWhen,
  PlanSupplement,
  SupplementDose,
  SupplementFrequency,
  SupplementPayload,
  SupplementTimePayload,
  SupplementTimingParam,
  SupplementPlanDetailResponse,
  SupplementPlanRecord,
  SupplementPlanState,
  SupplementPlanStatusParam,
  SupplementPlanSummary,
  SupplementPlanSummaryResponse,
  SupplementResponse,
  SupplementTimeResponse,
} from "./types/supplements.types";

/** The largest page the endpoint will serve. Anything above is clamped to it. */
export const MAX_PAGE_SIZE = 100;

/**
 * What a list call asks for unless it says otherwise. Ten, matching the ten
 * rows the table shows, so a page of the API is a page of the table.
 */
export const DEFAULT_PAGE_SIZE = 10;

/** `ACTIVE` is a plan the client has; anything else is still being written. */
export const toSupplementPlanStatus = (
  status: SupplementPlanStatusParam | null | undefined
): SupplementPlanState => (status === "ACTIVE" ? "sent" : "draft");

/**
 * And back again, for the list's `status` filter. `"all"` is both, which is
 * asked for by leaving the parameter off — the All tab.
 */
export const toSupplementPlanStatusParam = (
  status: SupplementPlanState | "all"
): SupplementPlanStatusParam | undefined => {
  if (status === "draft") return "DRAFT";
  if (status === "sent") return "ACTIVE";
  return undefined;
};

/**
 * `"MON"` and the rest, as the index that day has in `DAY_LABELS`.
 *
 * Built off the labels rather than written out beside them, so the week is
 * ordered in one place: the toggles, the frequency line and this all read
 * Monday as 0 because they all read the same array.
 */
const DAY_INDEX = new Map(
  DAY_LABELS.map((label, index) => [label.toUpperCase(), index])
);

/**
 * The days a supplement is taken on, as indexes.
 *
 * A day the map has never heard of is dropped rather than guessed at — an
 * index of -1 would draw as a toggle that is on and belongs to no day.
 */
const toDays = (days: string[] | null | undefined): number[] =>
  (days ?? [])
    .map((day) => DAY_INDEX.get(String(day).trim().toUpperCase()))
    .filter((index): index is number => index !== undefined)
    .sort((a, b) => a - b);

/**
 * `EVERYDAY` against the days themselves.
 *
 * A supplement that says `SPECIFIC_DAYS` and names none is read as everyday
 * rather than as a schedule with nothing in it: the frequency is the answer
 * the backend gave, and an empty week would draw as a card nobody can take.
 */
const toFrequency = (
  supplement: SupplementResponse
): SupplementFrequency =>
  supplement?.frequency === "SPECIFIC_DAYS" &&
  toDays(supplement?.days_of_week).length > 0
    ? "days"
    : "everyday";

/** `BEFORE` against `Before`. Anything else reads as after the meal. */
const toWhen = (timing: string | null | undefined): DoseWhen =>
  String(timing).toUpperCase() === "BEFORE" ? "Before" : "After";

/**
 * `"08:00:00"` as `"08:00 am"` — the way `TIME_OPTIONS` writes one, so a
 * fetched dose sits in the list rather than beside it.
 *
 * A time the endpoint sends that is not on the half hour is kept as it is
 * rather than rounded onto one: the dropdown will not hold it, but the card
 * still reads back what the plan actually says.
 */
export const toClockLabel = (time: string | null | undefined): string => {
  const [rawHour, rawMinute] = String(time ?? "").split(":");
  const hour24 = Number(rawHour);
  const minute = String(rawMinute ?? "").padStart(2, "0");

  if (!Number.isInteger(hour24) || hour24 < 0 || hour24 > 23 || !rawMinute)
    return "";

  // Midnight and noon are both 12 on a twelve-hour clock, which is the one
  // case a plain modulo gets wrong.
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const period = hour24 < 12 ? "am" : "pm";

  return `${String(hour12).padStart(2, "0")}:${minute.slice(0, 2)} ${period}`;
};

/** One row of TIMES PER DAY, as the card holds one. */
const toDose = (time: SupplementTimeResponse): SupplementDose => ({
  id: createId("dose"),
  when: toWhen(time?.timing),
  meal: time?.meal_label?.trim() ?? "",
  // Absent and null mean the same thing: a dose no catalog row is behind.
  mealSlotTypeId: time?.meal_slot_type_id ?? null,
  time: toClockLabel(time?.time),
});

/** The times on a supplement, in the order the backend put them in. */
const toDoses = (
  times: SupplementTimeResponse[] | null | undefined
): SupplementDose[] =>
  [...(times ?? [])]
    .sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0))
    .map(toDose);

/**
 * One supplement on a plan, as a card reads one.
 *
 * Every field is defended: a supplement with no unit, no times or no name is
 * one the screen still has to draw, and a blank on a card is a better answer
 * than a screen that fails.
 */
const toPlanSupplement = (
  supplement: SupplementResponse
): PlanSupplement => ({
  id: createId("supplement"),
  name: supplement?.name?.trim() ?? "",
  dosage: supplement?.dosage_strength?.trim() ?? "",
  unit: supplement?.unit_name?.trim() ?? "",
  unitId: supplement?.unit_id ?? null,
  frequency: toFrequency(supplement),
  // Everyday is stored as no days at all, and drawn as all of them — the
  // toggles are on screen whichever the frequency says, and a row of empty
  // ones under "Everyday" would read as a contradiction.
  days:
    toFrequency(supplement) === "everyday"
      ? DAY_LABELS.map((_, index) => index)
      : toDays(supplement?.days_of_week),
  doses: toDoses(supplement?.times),
  note: supplement?.notes?.trim() ?? "",
});

/** The supplements on a plan, in the order the backend put them in. */
const toPlanSupplements = (
  supplements: SupplementResponse[] | null | undefined
): PlanSupplement[] =>
  [...(supplements ?? [])]
    .sort((a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0))
    .map(toPlanSupplement);

/**
 * One row, in the shape the table reads.
 *
 * Every field is defended the same way: a plan with no client, no date or no
 * names is a row the list still has to render, and a dash in a cell is a
 * better answer than a screen that fails to draw. If the backend's field names
 * turn out to differ from these, this function is the only thing that changes.
 */
export const toSupplementPlanSummary = (
  row: SupplementPlanSummaryResponse
): SupplementPlanSummary => {
  const name = row?.name?.trim() || "Untitled plan";

  return {
    id: row?.public_id ?? "",
    name,
    shortName: shortPlanName(name),
    clientId: row?.client?.public_id ?? "",
    clientName: row?.client?.name?.trim() || "Unnamed client",
    status: toSupplementPlanStatus(row?.status),
    createdOn: row?.created_at ?? null,
    activatedOn: row?.activated_at ?? null,
    // The backend's own count rather than the length of the names beside it:
    // the row is the authority on how many supplements are on the plan.
    supplementCount: row?.supplement_count ?? 0,
    names: (row?.supplement_names ?? [])
      .map((supplementName) => supplementName?.trim() ?? "")
      .filter(Boolean),
  };
};

/** One plan whole, which is a row plus what is actually on it. */
export const toSupplementPlanRecord = (
  detail: SupplementPlanDetailResponse
): SupplementPlanRecord => {
  const supplements = toPlanSupplements(detail?.supplements);

  return {
    ...toSupplementPlanSummary(detail),
    // The detail carries the supplements, so its own names beat the summary's
    // reading of `supplement_names` — same names, one source.
    names: supplements.map((supplement) => supplement.name).filter(Boolean),
    supplements,
  };
};

/* ── What goes up ───────────────────────────────────────────────────────── */

/**
 * `Before` against `BEFORE`. The inverse of `toWhen`. An empty one — a dose
 * still being written — never reaches here: `isSupplementComplete` holds the
 * card until every row has picked one.
 */
const toTimingParam = (when: SupplementDose["when"]): SupplementTimingParam =>
  when === "Before" ? "BEFORE" : "AFTER";

/**
 * `"08:00 am"` as `"08:00"` — the inverse of `toClockLabel`, and what the
 * write sends.
 *
 * Seconds are not sent: the endpoint takes `HH:MM` and answers `HH:MM:SS`,
 * which is the backend's business rather than the screen's. A label that is
 * not one of `TIME_OPTIONS` — which only a fetched plan can produce — is
 * passed through as it is rather than guessed at.
 */
export const toWireTime = (label: string): string => {
  const match = /^(\d{1,2}):(\d{2})\s*(am|pm)$/i.exec(label.trim());
  if (!match) return label.trim();

  const [, rawHour, minute, period] = match;
  const hour12 = Number(rawHour) % 12;
  // Midnight and noon are the one case a plain modulo gets wrong, in the other
  // direction this time: 12 am is hour 0, and 12 pm is hour 12.
  const hour24 = period.toLowerCase() === "pm" ? hour12 + 12 : hour12;

  return `${String(hour24).padStart(2, "0")}:${minute}`;
};

/** The days it is taken on, as the three-letter names the wire uses. */
const toWireDays = (days: number[]): string[] =>
  [...days]
    .sort((a, b) => a - b)
    .map((day) => DAY_LABELS[day]?.toUpperCase())
    .filter(Boolean);

/**
 * The times on one supplement, as a write sends them.
 *
 * A dose still being written is dropped: a row opens with nothing picked on
 * it, and Save as draft files a card as it stands, saved or not — so a draft
 * can carry a row with no meal to put in `meal_slot_type`, which is required,
 * or no time worth sending. A row that says nothing yet is left off rather
 * than sent as a dose the plan never named. A sent plan never gets here with
 * one: `isSupplementComplete` holds the card until every row is whole.
 */
const toTimePayloads = (doses: SupplementDose[]): SupplementTimePayload[] =>
  doses
    .filter(isDoseComplete)
    .map((dose) => ({
      timing: toTimingParam(dose.when),
      meal_slot_type: dose.mealSlotTypeId as number,
      time: toWireTime(dose.time),
    }));

/**
 * One card of the screen, as a create or an update takes it.
 *
 * `days_of_week` only where the frequency is the days themselves, and `notes`
 * only where there is one — see `SupplementPayload`, which is where the two
 * omissions are argued. The unit goes up as the catalog row's id: a card whose
 * unit was never resolved to one cannot be sent, which is what
 * `isSupplementComplete` holds the Save button on.
 */
export const toSupplementPayload = (
  supplement: PlanSupplement
): SupplementPayload => {
  const payload: SupplementPayload = {
    name: supplement.name.trim(),
    dosage_strength: supplement.dosage.trim(),
    unit: supplement.unitId as number,
    frequency:
      supplement.frequency === "days" ? "SPECIFIC_DAYS" : "EVERYDAY",
    times: toTimePayloads(supplement.doses),
  };

  if (supplement.frequency === "days") {
    payload.days_of_week = toWireDays(supplement.days);
  }

  const note = supplement.note.trim();
  if (note) payload.notes = note;

  return payload;
};

/**
 * Every card worth storing, in the order they are on the page.
 *
 * A card with no name is dropped: it is one the dietitian opened and did not
 * use, not a supplement with no name. The screen drops these too, so this is a
 * second guard rather than the only one — but this is the one the request
 * passes through.
 */
export const toSupplementPayloads = (
  supplements: PlanSupplement[]
): SupplementPayload[] =>
  supplements
    .filter((supplement) => supplement.name.trim())
    .map(toSupplementPayload);
